import { Direction2D } from '@paragon/geometry-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { getSelectedItemIdentifiers } from '../../library/selection/selectors';
import { isBearingEnvelopeSelectableType } from '../../library/selection/types';
import { RootState } from '..';
import { shouldInteractiveEditingBeDisabled } from './interactiveEditingSelectors';

/**
 * Returns the cursor to use for the scene canvas, or null to use the default cursor.
 */
export function getSceneCanvasCursorOverride(state: RootState): string | undefined {
  if (shouldInteractiveEditingBeDisabled(state)) return undefined;

  const { activeEdit } = state.interactiveEditing;
  const { hoveredBearingEnvelopeEdge, hoveredBearingEnvelopeInterior } = state.layout;

  if (activeEdit) {
    switch (activeEdit.type) {
      case 'bearingEnvelopeLength': {
        const { baseEnvelope } = activeEdit;
        const envelopeDirection = Direction2D.fromPointObjects(baseEnvelope.leftPoint, baseEnvelope.rightPoint);
        return getResizeCursorForEnvelopeEdge(envelopeDirection.rotateQuarterTurn().angle);
      }

      case 'bearingEnvelopeMove':
        return 'move';

      default:
        assertExhaustiveSwitch(activeEdit);
    }
  }

  if (hoveredBearingEnvelopeEdge && isBearingEnvelopeSelected(state, hoveredBearingEnvelopeEdge.guid)) {
    return getResizeCursorForEnvelopeEdge(hoveredBearingEnvelopeEdge.edgeAngle);
  }

  if (hoveredBearingEnvelopeInterior && isBearingEnvelopeSelected(state, hoveredBearingEnvelopeInterior.guid)) {
    return 'move';
  }

  return undefined;
}

function isBearingEnvelopeSelected(state: RootState, guid: string) {
  return getSelectedItemIdentifiers(state).some(
    (item) => item.guid === guid && isBearingEnvelopeSelectableType(item.type),
  );
}

/**
 * Determines the appropriate resize cursor for an envelope edge, given its angle (in degrees).
 */
function getResizeCursorForEnvelopeEdge(angle: number) {
  const normalizedAngle = ((angle % 180) + 180) % 180;

  if (normalizedAngle < 22.5 || normalizedAngle >= 157.5) {
    return 'ns-resize';
  } else if (normalizedAngle >= 22.5 && normalizedAngle < 67.5) {
    return 'nwse-resize';
  } else if (normalizedAngle >= 67.5 && normalizedAngle < 112.5) {
    return 'ew-resize';
  } else {
    return 'nesw-resize';
  }
}
